import { useState, useEffect, useRef } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useChatWithContext } from '@/hooks/useChatWithContext';
import { useDeleteChat } from '@/hooks/useClientChat';
import { usePageContext } from '@/hooks/usePageContext';
import { useCapabilities } from '@/hooks/useCapabilities';
import { ChatSidebar } from './ChatSidebar';
import { ChatMessages } from './ChatMessages';
import { ChatInput } from './ChatInput';
import { ScopeSelector } from './ScopeSelector';
import { MessageSquare, History, ChevronLeft } from 'lucide-react';
import { CONTEXT_SOURCE_LABELS, type ChatContextSource } from '@/types/chat';

interface GlobalChatPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Override auto-detected context */
  initialClientId?: string;
  initialClientName?: string;
  /** Pre-fill the first message (from Cmd+K) */
  initialMessage?: string;
}

/** Base context sources available to all roles */
const BASE_SOURCES: ChatContextSource[] = [
  'client_details',
  'recent_analyses',
  'active_tasks',
  'client_goals',
  'emails',
  'slack',
  'clickup_tasks',
];

/** Additional sources for managers and admins */
const MANAGER_SOURCES: ChatContextSource[] = [
  'financials',
  'bd_pipeline',
  'team_performance',
  'contractor_rates',
];

/** Additional source for admins only */
const ADMIN_SOURCES: ChatContextSource[] = ['audit_log'];

/**
 * GlobalChatPanel — the primary Ask Poco chat interface.
 *
 * Supports both client-scoped and org-wide conversations.
 * Auto-detects the current page context via usePageContext, with
 * optional prop overrides for explicit scoping.
 *
 * @param open - Controls sheet visibility
 * @param onOpenChange - Callback when sheet open state changes
 * @param initialClientId - Override auto-detected client ID
 * @param initialClientName - Override auto-detected client name
 * @param initialMessage - Pre-fill and auto-send on open (from Cmd+K)
 */
export function GlobalChatPanel({
  open,
  onOpenChange,
  initialClientId,
  initialClientName,
  initialMessage,
}: GlobalChatPanelProps) {
  const [showHistory, setShowHistory] = useState(false);
  const [scopeOverride, setScopeOverride] = useState<{ id: string; name: string } | null>(null);
  const initialSentRef = useRef(false);

  const pageContext = usePageContext();
  const { hasCapability } = useCapabilities();

  // Priority: prop overrides > user scope override > auto-detected page context
  const clientId = initialClientId ?? scopeOverride?.id ?? pageContext.clientId;
  const clientName = initialClientName ?? scopeOverride?.name ?? pageContext.clientName;
  const scope = clientId ? 'client' : 'global';

  const {
    chats,
    chatsLoading,
    messages,
    messagesLoading,
    activeChatId,
    sendMessage,
    startNewChat,
    selectChat,
    isSending,
    sendError,
  } = useChatWithContext(clientId ?? null, scope);

  const deleteChat = useDeleteChat();

  // Reset scope override when panel closes
  useEffect(() => {
    if (!open) {
      setScopeOverride(null);
    }
  }, [open]);

  const handleScopeChange = (client: { id: string; name: string } | null) => {
    setScopeOverride(client);
    startNewChat();
  };

  // Auto-send initialMessage once when the panel opens
  useEffect(() => {
    if (!open) {
      // Reset the sent flag so the message can fire again next open if needed
      initialSentRef.current = false;
      return;
    }

    if (initialMessage && !initialSentRef.current) {
      initialSentRef.current = true;
      void sendMessage(initialMessage);
    }
  }, [open, initialMessage, sendMessage]);

  const handleDeleteChat = (chatId: string) => {
    deleteChat.mutate(
      { chatId, clientId },
      {
        onSuccess: () => {
          if (activeChatId === chatId) {
            startNewChat();
          }
        },
      },
    );
  };

  const handleSelectChat = (chatId: string) => {
    selectChat(chatId);
    setShowHistory(false);
  };

  const handleNewChat = () => {
    startNewChat();
    setShowHistory(false);
  };

  // Build the list of context sources based on role
  const contextSources: ChatContextSource[] = [
    ...BASE_SOURCES,
    ...(hasCapability('canViewFinancials') ? MANAGER_SOURCES : []),
    ...(hasCapability('canViewSecuritySettings') ? ADMIN_SOURCES : []),
  ];

  const historyDescription =
    scope === 'client' && clientName
      ? `Previous conversations about ${clientName}`
      : 'Previous organization-wide conversations';

  const chatPlaceholder =
    scope === 'client' && clientName
      ? `Ask about ${clientName}...`
      : 'Ask Poco anything about your organization...';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col p-0">
        <SheetHeader className="px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            {showHistory && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => setShowHistory(false)}
                aria-label="Back to chat"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}

            <div className="flex-1 min-w-0">
              <SheetTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 shrink-0" />
                {showHistory ? 'Chat History' : 'Ask Poco'}
              </SheetTitle>
              <SheetDescription asChild>
                <div className="mt-1">
                  {showHistory ? (
                    <span className="text-sm text-muted-foreground">{historyDescription}</span>
                  ) : (
                    <ScopeSelector
                      scope={scope}
                      clientName={clientName ?? null}
                      onScopeChange={handleScopeChange}
                    />
                  )}
                </div>
              </SheetDescription>
            </div>

            {!showHistory && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 shrink-0"
                onClick={() => setShowHistory(true)}
                aria-label="View chat history"
              >
                <History className="h-4 w-4" />
                History
              </Button>
            )}
          </div>
        </SheetHeader>

        {showHistory ? (
          <ChatSidebar
            chats={chats}
            activeChatId={activeChatId}
            isLoading={chatsLoading}
            onSelectChat={handleSelectChat}
            onNewChat={handleNewChat}
            onDeleteChat={handleDeleteChat}
          />
        ) : (
          <>
            {/* Context sources bar */}
            <div className="px-4 py-2 border-b bg-muted/30">
              <p className="text-xs text-muted-foreground">
                Poco has context from:{' '}
                {contextSources.map((source) => CONTEXT_SOURCE_LABELS[source]).join(', ')}
              </p>
            </div>

            {/* Messages area */}
            <ChatMessages
              messages={messages}
              isLoading={messagesLoading}
              isSending={isSending}
              sendError={sendError}
            />

            {/* Input area */}
            <ChatInput
              onSend={sendMessage}
              isSending={isSending}
              placeholder={chatPlaceholder}
            />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
